/**
 * eTrainu-style online training: one record per (active staff × assigned
 * course), generated deterministically from the roster. The compliance
 * categories (compliance.ts) read the SAME records, so a course that shows
 * 12 expired accreditations on the Training page shows 12 expired on the
 * Workplace Compliance page.
 */
import { Quarter } from '../../clock';
import { rng } from '../../rng';
import { lastSyncedAt } from '../../state';
import { Staff } from '../../world';
import { COMPLIANCE_COURSES, gapFor, untrackedStaff } from './gaps';
import {
    activeOn,
    activeToday,
    daily,
    daysBetween,
    isCare,
    isFrontline,
    isRemote,
    pct1,
    round1,
    shiftDay,
    shiftMonths,
    tenureDays,
    today,
    trendWindow,
} from './common';

export interface CourseDef {
    name: string;
    /** Accreditation lifetime; null = never expires. */
    validityMonths: number | null;
    /** Probability an assigned record is complete (including since-lapsed accreditations). */
    pComplete: number;
    /** Of completions, the share whose accreditation has lapsed. */
    pExpired: number;
    applies: (s: Staff) => boolean;
}

const all = () => true;

export const COURSES: CourseDef[] = [
    { name: 'NDIS Worker Orientation Module', validityMonths: null, pComplete: 0.965, pExpired: 0, applies: all },
    { name: 'Cultural Safety', validityMonths: 36, pComplete: 0.95, pExpired: 0.004, applies: all },
    { name: 'Infection Control', validityMonths: 24, pComplete: 0.915, pExpired: 0.012, applies: all },
    { name: 'Manual Handling', validityMonths: 24, pComplete: 0.905, pExpired: 0.012, applies: all },
    { name: 'DFV Awareness', validityMonths: null, pComplete: 0.83, pExpired: 0, applies: all },
    { name: 'Privacy & Confidentiality', validityMonths: null, pComplete: 0.86, pExpired: 0, applies: all },
    { name: 'Complaints Handling', validityMonths: null, pComplete: 0.85, pExpired: 0, applies: all },
    { name: 'Incident Management', validityMonths: null, pComplete: 0.84, pExpired: 0, applies: all },
    { name: 'Fire Safety & Evacuation', validityMonths: 12, pComplete: 0.76, pExpired: 0.01, applies: all },
    { name: 'First Aid', validityMonths: 36, pComplete: 0.86, pExpired: 0.012, applies: isFrontline },
    { name: 'CPR', validityMonths: 12, pComplete: 0.79, pExpired: 0.03, applies: isFrontline },
    { name: 'Trauma-Informed Practice', validityMonths: null, pComplete: 0.7, pExpired: 0, applies: isFrontline },
    { name: 'Working with Interpreters', validityMonths: null, pComplete: 0.68, pExpired: 0, applies: isFrontline },
    { name: 'Mental Health First Aid', validityMonths: 36, pComplete: 0.6, pExpired: 0.01, applies: isFrontline },
    { name: 'Remote Driving 4WD', validityMonths: 36, pComplete: 0.66, pExpired: 0.012, applies: s => isFrontline(s) && isRemote(s) },
    { name: 'Medication Administration', validityMonths: 24, pComplete: 0.81, pExpired: 0.015, applies: isCare },
    { name: 'Restrictive Practices', validityMonths: null, pComplete: 0.75, pExpired: 0, applies: s => s.program === 'Disability Services (NDIS)' },
    { name: 'Elder Abuse Awareness', validityMonths: null, pComplete: 0.78, pExpired: 0, applies: s => s.program === 'Aged Care' },
    {
        name: 'Child Safe Practice',
        validityMonths: null,
        pComplete: 0.8,
        pExpired: 0,
        applies: s => s.program === 'Youth Services' || s.program === 'Child Nutrition' || s.program === 'Domestic & Family Violence',
    },
    { name: 'Safe Food Handling', validityMonths: 36, pComplete: 0.82, pExpired: 0.01, applies: s => s.program === 'Child Nutrition' || s.program === 'Aged Care' },
];

export type TrainingStatus = 'complete' | 'in_progress' | 'not_started';

export interface TrainingRecord {
    staff: Staff;
    course: string;
    status: TrainingStatus;
    completionDate: string | null;
    expiryDate: string | null;
    progress: string | null;
}

/** Days until the accreditation lapses (negative = overdue); null when it never expires or isn't complete. */
export function daysRemaining(r: TrainingRecord, t = today()): number | null {
    return r.status === 'complete' && r.expiryDate ? daysBetween(t, r.expiryDate) : null;
}

export { untrackedStaff } from './gaps';

/**
 * One staff × course record. Compliance courses follow the gap plan (gaps.ts):
 * staff outside it are current, staff inside it have exactly the planned gap.
 * Other courses (professional development) keep their own completion odds.
 */
function makeRecord(s: Staff, c: CourseDef, t: string): TrainingRecord {
    const r = rng(`people:training:${s.id}:${c.name}`);
    const fresh = tenureDays(s, t) < 120;
    const base: TrainingRecord = { staff: s, course: c.name, status: 'not_started', completionDate: null, expiryDate: null, progress: null };
    const tracked = COMPLIANCE_COURSES.has(c.name);
    let gap = tracked ? gapFor(s.id, c.name) : null;
    if (gap === 'expired' && (!c.validityMonths || shiftMonths(shiftDay(t, -120), -c.validityMonths) < s.startDate)) {
        gap = 'missing'; // too new to have let an accreditation lapse
    }
    if (gap === 'in_progress') {
        return { ...base, status: 'in_progress', progress: `${r.int(2, 8) * 10}%` };
    }
    const completes = tracked ? gap !== 'missing' : r.next() < (fresh ? c.pComplete * 0.75 : c.pComplete);
    if (completes) {
        const lo = shiftDay(s.startDate, 3) < t ? shiftDay(s.startDate, 3) : shiftDay(t, -1);
        const hi = shiftDay(t, -1);
        if (c.validityMonths) {
            const u = r.next();
            const pExpired = tracked ? (gap === 'expired' ? 1 : 0) : c.pExpired;
            const pExpiring = 0.22 * Math.min(1, 3 / c.validityMonths);
            let expiry: string | null = null;
            if (u < pExpired) {
                expiry = shiftDay(t, -r.int(3, 120));
            } else if (u < pExpired + pExpiring) {
                expiry = shiftDay(t, r.int(1, 90));
            }
            if (expiry && shiftMonths(expiry, -c.validityMonths) >= s.startDate) {
                return { ...base, status: 'complete', completionDate: shiftMonths(expiry, -c.validityMonths), expiryDate: expiry };
            }
            // Comfortably current: completed recently enough to expire > 90 days out.
            const floor = shiftDay(shiftMonths(t, -c.validityMonths), 91);
            const from = lo > floor ? lo : floor;
            const completion = from <= hi ? shiftDay(from, r.int(0, daysBetween(from, hi))) : hi;
            return { ...base, status: 'complete', completionDate: completion, expiryDate: shiftMonths(completion, c.validityMonths) };
        }
        const completion = lo <= hi ? shiftDay(lo, r.int(0, daysBetween(lo, hi))) : hi;
        return { ...base, status: 'complete', completionDate: completion };
    }
    if (!tracked && r.next() < 0.08) {
        return { ...base, status: 'in_progress', progress: `${r.int(1, 9) * 10}%` };
    }
    return base;
}

/** Every eTrainu record for currently active, enrolled staff. */
export function trainingRecords(): TrainingRecord[] {
    return daily('people:training:records', () => {
        const t = today();
        const skip = new Set(untrackedStaff().map(s => s.id));
        const out: TrainingRecord[] = [];
        for (const s of activeToday()) {
            if (skip.has(s.id)) {
                continue;
            }
            for (const c of COURSES) {
                if (c.applies(s)) {
                    out.push(makeRecord(s, c, t));
                }
            }
        }
        return out;
    });
}

/** Records keyed `${staffId}|${course}` for compliance lookups. */
export function trainingIndex(): Map<string, TrainingRecord> {
    return daily('people:training:index', () => new Map(trainingRecords().map(r => [`${r.staff.id}|${r.course}`, r])));
}

function buildTrainingCore() {
    const t = today();
    const records = trainingRecords();
    const complete = records.filter(r => r.status === 'complete');
    const inProgress = records.filter(r => r.status === 'in_progress');

    const expiringList = complete
        .map(r => ({ r, days: daysRemaining(r, t) }))
        .filter(x => x.days !== null && x.days <= 60)
        .sort((a, b) => a.days! - b.days! || a.r.staff.name.localeCompare(b.r.staff.name))
        .map(({ r, days }) => ({
            staff_name: r.staff.name,
            course_name: r.course,
            accreditation_expiry_date: r.expiryDate,
            course_expiry_date: null as string | null,
            days_remaining: days!,
            status: days! < 0 ? 'expired' : 'expiring',
        }));

    const byCourse = COURSES.map(c => {
        const rs = records.filter(r => r.course === c.name);
        return {
            courseName: c.name,
            total: rs.length,
            complete: rs.filter(r => r.status === 'complete').length,
            in_progress: rs.filter(r => r.status === 'in_progress').length,
        };
    })
        .filter(c => c.total > 0)
        .sort((a, b) => b.total - a.total || a.courseName.localeCompare(b.courseName));

    const perStaff = new Map<number, { staffName: string; total: number; complete: number; in_progress: number }>();
    for (const r of records) {
        const row = perStaff.get(r.staff.id) ?? { staffName: r.staff.name, total: 0, complete: 0, in_progress: 0 };
        row.total++;
        if (r.status === 'complete') {
            row.complete++;
        } else if (r.status === 'in_progress') {
            row.in_progress++;
        }
        perStaff.set(r.staff.id, row);
    }
    const staffProgress = [...perStaff.values()]
        .map(p => ({ ...p, completion_percent: pct1(p.complete, p.total) }))
        .sort((a, b) => b.completion_percent - a.completion_percent || a.staffName.localeCompare(b.staffName));

    return {
        total: records.length,
        complete: complete.length,
        in_progress: inProgress.length,
        completion_rate: pct1(complete.length, records.length),
        staff_count: perStaff.size,
        course_count: byCourse.length,
        expiring_soon: complete.filter(r => {
            const d = daysRemaining(r, t);
            return d !== null && d >= 0 && d <= 90;
        }).length,
        expired: complete.filter(r => {
            const d = daysRemaining(r, t);
            return d !== null && d < 0;
        }).length,
        expiring_list: expiringList,
        by_course: byCourse,
        staff_progress: staffProgress,
        staff_progress_total: staffProgress.length,
        staff_in_progress: inProgress
            .map(r => ({ staff_name: r.staff.name, course_name: r.course, progress: r.progress }))
            .sort((a, b) => a.staff_name.localeCompare(b.staff_name)),
        untracked_staff: untrackedStaff()
            .map(s => s.name)
            .sort(),
        unmatched_training_staff: [] as string[],
    };
}

export type TrainingStats = ReturnType<typeof buildTrainingCore> & { last_synced_at: string | null };

/** GET /api/training/stats (board twin keeps only the headline fields). */
export function trainingStats(board = false): TrainingStats | Record<string, unknown> {
    const core = daily('people:training:core', buildTrainingCore);
    const last = lastSyncedAt('training');
    if (board) {
        return { staff_count: core.staff_count, completion_rate: core.completion_rate, expiring_soon: core.expiring_soon, expired: core.expired, last_synced_at: last };
    }
    return { ...core, last_synced_at: last };
}

/** Full (non-board) training stats, typed, for other generators. */
export function trainingSummary(): TrainingStats {
    return trainingStats(false) as TrainingStats;
}

/** One daily history row for /api/trends/training; today's row equals trainingStats(). */
export function trainingTrendRow(day: string): Record<string, unknown> {
    const s = trainingSummary();
    const t = today();
    const back = Math.max(0, daysBetween(day, t));
    const untracked = s.untracked_staff.length;
    // Same definition as the tile: complete accreditations lapsing within 90 days.
    const expiring90 = s.expiring_soon;
    if (back === 0) {
        return {
            date: day,
            total: s.total,
            total_records: s.total,
            complete: s.complete,
            in_progress: s.in_progress,
            completion_rate: s.completion_rate,
            staff_tracked: s.staff_count,
            course_count: s.course_count,
            expired: s.expired,
            expiring_90_days: expiring90,
            untracked_staff: untracked,
            unmatched_training_staff: 0,
        };
    }
    const r = rng(`people:training:trend:${day}`);
    const tracked = Math.max(1, activeOn(day).length - untracked);
    const rate = round1(Math.min(99, Math.max(40, s.completion_rate - back * 0.012 + r.around(0, 0.3, -1, 1, 2))));
    const total = Math.round((s.total * tracked) / Math.max(1, s.staff_count));
    return {
        date: day,
        total,
        total_records: total,
        complete: Math.round((total * rate) / 100),
        in_progress: Math.max(0, Math.round(s.in_progress + r.around(0, 4) + Math.sin(back / 11) * 3)),
        completion_rate: rate,
        staff_tracked: tracked,
        course_count: s.course_count,
        expired: Math.max(0, Math.round(s.expired + Math.sin(back / 9) * 3 + r.around(0, 1.5))),
        expiring_90_days: Math.max(0, Math.round(expiring90 + Math.cos(back / 13) * 5 + r.around(0, 2))),
        untracked_staff: untracked,
        unmatched_training_staff: 0,
    };
}

/** GET /api/trends/training?quarter=… | ?days=N */
export function trainingTrends(q: Quarter | null, daysParam: string | null = null): Record<string, unknown> {
    const w = trendWindow(q, daysParam);
    const data = w.dates.map(trainingTrendRow);
    return { source: 'training', days: w.days, quarter: w.quarter, range: w.range, count: data.length, data };
}
